import type { LiveCoding } from "@hireroo/app-helper/hooks";
import { RemotesId } from "@hireroo/app-store/page/c/remotes_id";
import { RemoteInterviewParticipants } from "@hireroo/app-store/widget/shared/RemoteInterviewParticipants";
import { RemoteInterviewTutorial } from "@hireroo/app-store/widget/shared/RemoteInterviewTutorial";
import { useChallengeCodingEditorContext } from "@hireroo/challenge/store";
import { getGraphqlClient } from "@hireroo/graphql/client/request";
import { useSystemDesignContext } from "@hireroo/system-design/react/FlowChart";
import * as Sentry from "@sentry/react";
import * as React from "react";

type ConnectRealtimeHooksToRemotesIdStoreArgs = {
  collaborativeState: LiveCoding.CollaborativeState;
};

export const useConnectRealtimeHooksToRemotesIdStore = (args: ConnectRealtimeHooksToRemotesIdStoreArgs) => {
  const { collaborativeState } = args;
  const challengeCodingEditor = useChallengeCodingEditorContext();
  const activeSessionVariant = RemotesId.useActiveSessionRemoteQuestionVariant();
  const systemDesign = useSystemDesignContext();

  React.useEffect(() => {
    RemotesId.updateActiveSessionId(collaborativeState.selectedSession);
  }, [collaborativeState.selectedSession]);

  React.useEffect(() => {
    RemotesId.updateQuestionVariant(collaborativeState.selectedQuestionVariant);
  }, [collaborativeState.selectedQuestionVariant]);

  React.useEffect(() => {
    if (activeSessionVariant === "ALGORITHM" || activeSessionVariant === "DATABASE" || activeSessionVariant === "CLASS") {
      challengeCodingEditor.action.updateLanguage(collaborativeState.selectedLanguage, "PASSIVE");
    }
  }, [activeSessionVariant, challengeCodingEditor, collaborativeState.selectedLanguage]);

  React.useEffect(() => {
    systemDesign.action.updateComponentType(collaborativeState.selectedComponentType);
  }, [collaborativeState.selectedComponentType, systemDesign.action]);
};

type ConnectRealtimeHooksToRemoteInterviewParticipantsArgs = {
  collaborativeState: LiveCoding.CollaborativeState;
};

export const useConnectRealtimeHooksToRemoteInterviewParticipantsStore = (args: ConnectRealtimeHooksToRemoteInterviewParticipantsArgs) => {
  const { collaborativeState } = args;
  React.useEffect(() => {
    const participants = Object.values(collaborativeState.users).map((user): RemoteInterviewParticipants.Participant => {
      return {
        id: user.uid,
        displayName: user.name,
        src: null,
      };
    });
    RemoteInterviewParticipants.updateParticipants(participants);
  }, [collaborativeState.users]);
};

/**
 * This hooks is a function that takes measures to prevent GetRemotes from being repeatedly
 * executed when caught updating the realtime database.
 */
export const useFetchRemote = (props: { remoteId: string }) => {
  const client = getGraphqlClient();
  const [fetchingStatus, setFetchingStatus] = React.useState<"START" | "PENDING" | "STOP">("STOP");
  const fetchRemote = React.useCallback(() => {
    setFetchingStatus("PENDING");
    client
      .GetRemotesForCandidate({
        remoteId: props.remoteId,
      })
      .then(res => {
        RemotesId.updateRemote(res.remote);
      })
      .catch(error => {
        Sentry.captureException(error);
      })
      .finally(() => {
        setFetchingStatus("STOP");
      });
  }, [client, props.remoteId]);

  React.useEffect(() => {
    if (fetchingStatus === "START") {
      fetchRemote();
    }
  }, [fetchRemote, fetchingStatus]);

  return {
    start: () => {
      setFetchingStatus("START");
    },
  };
};

const TutorialMap: Record<RemotesId.LiveCodingQuestionVariant, RemoteInterviewTutorial.Kind> = {
  ALGORITHM: "CANDIDATE_CHALLENGE",
  DATABASE: "CANDIDATE_CHALLENGE",
  CLASS: "CANDIDATE_CHALLENGE",
  SYSTEM_DESIGN: "CANDIDATE_SYSTEM_DESIGN",
  UNKNOWN: "SKIP",
};

export const useAutoStartTutorial = (activeSessionVariant: RemotesId.LiveCodingQuestionVariant) => {
  React.useEffect(() => {
    RemoteInterviewTutorial.autoStartTutorial(TutorialMap[activeSessionVariant]);
    return () => {
      RemoteInterviewTutorial.clear();
    };
  }, [activeSessionVariant]);
};
